/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "semantic-ui-react";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../../slices/currentUser/currentUserSlice.js";
import { globalLoadingOff, globalLoadingOn } from "../../slices/globalLoading/globalLoadingSlice.js";
import {
    checkIfStatisticAbsoluteOrRelative,
    codeMatchesPercentage,
    codeMatchesProportion,
    codeMatchesRelativeRisk,
    codeMatchesRiskDifference
} from "../compositionResourceFunctions/summaryOfFindingsFunctions.js";
import { DisplayFromFHIRXhtml } from "../../datatypeComponents/NarrativeEntry.jsx";
import { selectAllResources } from "../../slices/resourceCollection/resourceCollectionSlice.js";
import { fetchOutcomeVariables, selectAllOutcomeVariables, selectOutcomeVariablesStatus } from "../../slices/outcomeVariables/outcomeVariablesSlice.js";
import { selectComparativeEvidenceMap } from "../../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice.js";
import { selectColorScheme } from "../../slices/colorScheme/colorSchemeSlice.js";
import getIdFromReference from "../../functions/getIdFromReference.js";
import addFoisFromFlisInTargetResources from "../compositionResourceFunctions/addFoisFromFlisInTargetResources.js";
import { loadNetEffectContributions, selectAllNetEffectContributions, selectNetEffectContributionsStatus } from "../../slices/netEffectContributions/netEffectContributionsSlice.js";
import RateRelativeImportanceForUser from "./RateRelativeImportanceForUser.jsx";
import { fetchUserImportanceRatings, selectAllUserImportanceRatings, selectUserImportanceRatingsStatus } from "../../slices/userImportanceRatings/userImpotanceRatingsSlice.js";

const extractControlEventRate = (baselineRiskEvidence, controlEventRate, controlEventRateDisplay) => {
    if (baselineRiskEvidence?.statistic?.length > 0) {
        for (const stat of baselineRiskEvidence.statistic) {
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0) {
                if (codeMatchesProportion(stat.statisticType.coding[0].code)) {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                        controlEventRate = stat.quantity.value * 100;
                        controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                        break;
                    }
                }
                if (codeMatchesPercentage(stat.statisticType.coding[0].code)) {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                        controlEventRate = stat.quantity.value;
                        controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                        break;
                    }
                }
                if (stat.statisticType.coding[0].code === 'STATO:0000047') {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value) && stat.sampleSize) {
                        let denominator = stat.sampleSize.numberAnalyzed || stat.sampleSize.knownDataCount || stat.sampleSize.numberOfParticipants || 0;
                        if (denominator > 0) {
                            controlEventRate = (stat.quantity.value / denominator) * 100;
                            controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                            break;
                        }
                    }
                }
            }
        }
    }
    return [controlEventRate, controlEventRateDisplay];
}

const extractRiskDifferenceAndInterventionEventRateForUser = (controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
    riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
    riskDifferenceHighValueDisplay, comparativeEvidence, dispatch) => {
    if (comparativeEvidence?.statistic?.length > 0) {
        let riskDifferenceStatistic;
        let riskRatioStatistic;
        let oddsRatioStatistic;
        for (const stat of comparativeEvidence.statistic) {
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                codeMatchesRiskDifference(stat.statisticType.coding[0].code)) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    riskDifferenceStatistic = stat;
                }
            }
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                (codeMatchesRelativeRisk(stat.statisticType.coding[0].code) || //risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000677' || stat.statisticType.coding[0].code === 'TBD:0000026' || //hazard ratio currently treated like risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000678' || stat.statisticType.coding[0].code === 'TBD:0000028' || //prevalence ratio currently treated like risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000680' || stat.statisticType.coding[0].code === 'TBD:0000027' //incidence rate ratio currently treated like risk ratio
                )) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    riskRatioStatistic = stat;
                }
            }
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                (stat.statisticType.coding[0].code === 'STATO:0000182' || stat.statisticType.coding[0].code === 'C16932')) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    oddsRatioStatistic = stat;
                }
            }
        }
        if (oddsRatioStatistic) {
            let oddsRatio = oddsRatioStatistic.quantity.value;
            let controlOdds = controlEventRate / (100 - controlEventRate);
            let interventionOdds = controlOdds * oddsRatio;
            let interventionEventRate = (interventionOdds / (interventionOdds + 1)) * 100;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            riskDifference = interventionEventRate - controlEventRate;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            if (oddsRatioStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of oddsRatioStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            let lowORValue = Number(attributeEstimate.range.low.value);
                            let interventionOddsLow = controlOdds * lowORValue;
                            let interventionEventRateLow = (interventionOddsLow / (interventionOddsLow + 1)) * 100;
                            riskDifferenceLowValue = interventionEventRateLow - controlEventRate;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            let highRRValue = Number(attributeEstimate.range.high.value);
                            let interventionOddsHigh = controlOdds * highRRValue;
                            let interventionEventRateHigh = (interventionOddsHigh / (interventionOddsHigh + 1)) * 100;
                            riskDifferenceHighValue = interventionEventRateHigh - controlEventRate;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
        } else if (riskRatioStatistic) {
            let relativeRisk = riskRatioStatistic.quantity.value;
            let interventionEventRate = controlEventRate * relativeRisk;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            riskDifference = interventionEventRate - controlEventRate;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            if (riskRatioStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of riskRatioStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            let lowRRValue = Number(attributeEstimate.range.low.value);
                            riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            let highRRValue = Number(attributeEstimate.range.high.value);
                            riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
        } else if (riskDifferenceStatistic) {
            riskDifference = riskDifferenceStatistic.quantity.value * 100;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            interventionEventRate = controlEventRate + riskDifference;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            if (riskDifferenceStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of riskDifferenceStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
        }
        if (isNaN(riskDifference)) {
            if (!isNaN(controlEventRate)) {
                for (const stat of comparativeEvidence.statistic) {
                    if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 && stat.statisticType.coding[0].code) {
                        const statCode = stat.statisticType.coding[0].code;
                        if (checkIfStatisticAbsoluteOrRelative(statCode) === "absolute") {
                            if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                let absoluteDifference = stat.quantity.value * 100;
                                interventionEventRate = controlEventRate + absoluteDifference;
                                interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                riskDifference = absoluteDifference;
                                riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                if (stat.attributeEstimate?.length > 0) {
                                    for (const attributeEstimate of stat.attributeEstimate) {
                                        if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                            if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                                                riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                            }
                                            if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                                                riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                            }
                                            let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                            riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                        }
                                    }
                                }
                            }
                        } else if (checkIfStatisticAbsoluteOrRelative(statCode) === "relative") {
                            if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                let relativeDifference = stat.quantity.value;
                                interventionEventRate = controlEventRate * relativeDifference;
                                interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                riskDifference = interventionEventRate - controlEventRate;
                                riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                if (stat.attributeEstimate?.length > 0) {
                                    for (const attributeEstimate of stat.attributeEstimate) {
                                        if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                            if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                let lowRRValue = Number(attributeEstimate.range.low.value);
                                                riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                                                riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                            }
                                            if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                let highRRValue = Number(attributeEstimate.range.high.value);
                                                riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                                                riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                            }
                                            let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                            riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
        riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
        riskDifferenceHighValueDisplay];
}

const calculateNetEffectContributionWithConfidence = (riskDifference, relativeImportance, desirability, riskDifferenceLowValue, riskDifferenceHighValue) => {
    let netEffectContribution = NaN;
    let netEffectContributionLow = NaN;
    let netEffectContributionHigh = NaN;

    try {
        if (desirability === -1 || desirability === "-1" || desirability.toLowerCase() === 'undesirable') {
            desirability = -1;
        } else {
            desirability = 1;
        }
    } catch { desirability = 1; }
    netEffectContribution = riskDifference * relativeImportance * desirability;
    netEffectContribution = parseFloat(netEffectContribution.toFixed(2));
    netEffectContributionLow = riskDifferenceLowValue * relativeImportance * desirability;
    netEffectContributionLow = parseFloat(netEffectContributionLow.toFixed(2));
    netEffectContributionHigh = riskDifferenceHighValue * relativeImportance * desirability;
    netEffectContributionHigh = parseFloat(netEffectContributionHigh.toFixed(2));

    if (netEffectContributionLow < netEffectContributionHigh) {
        return [netEffectContribution, netEffectContributionLow, netEffectContributionHigh];
    } else {
        return [netEffectContribution, netEffectContributionHigh, netEffectContributionLow];
    }
};

const createNetEffectContributionDisplay = (netEffectContribution, netEffectContributionLow, netEffectContributionHigh) => {
    let netEffectContributionLowDisplay = "Not calculable";
    let netEffectContributionHighDisplay = "Not calculable";
    if (!isNaN(netEffectContribution)) {
        netEffectContribution = netEffectContribution / 100;
        if (!isNaN(netEffectContributionLow)) {
            netEffectContributionLow = netEffectContributionLow / 100;
            netEffectContributionLowDisplay = valueSpanWithStyle(netEffectContributionLow, 2, false);
        }
        if (!isNaN(netEffectContributionHigh)) {
            netEffectContributionHigh = netEffectContributionHigh / 100;
            netEffectContributionHighDisplay = valueSpanWithStyle(netEffectContributionHigh, 2, false);
        }
    }

    let netEffectContributionDisplay = valueSpanWithStyle(netEffectContribution, 2, true);
    if (netEffectContributionDisplay === "NaN") {
        netEffectContributionDisplay = "[Not calculable.]"
    }
    if (!isNaN(netEffectContributionLow) || !isNaN(netEffectContributionHigh)) {
        netEffectContributionDisplay += " (" + netEffectContributionLowDisplay + " to " + netEffectContributionHighDisplay + ")";
    }
    return netEffectContributionDisplay;
}

const valueSpanWithStyle = (value, decimalPoints = 2, bold = false) => {
    if (isNaN(value)) {
        return "[Not calculable.]"; //parseFloat(value).toString(); //value
    }
    let openSpan = "<span>";
    if (value !== 0) {
        let openColor = value > 0 ? "#00AA00" : "#AA0000";
        openSpan = '<span style="color: ' + openColor + '">';
    }
    if (bold) {
        return openSpan + "<b>" + parseFloat(value.toFixed(decimalPoints)).toString() + "</b></span>";
    } else {
        return openSpan + parseFloat(value.toFixed(decimalPoints)).toString() + "</span>";
    }
};

const loadParameterResources = async (dispatch, netEffectContributions,
    apiToken, combinedDictionary, resourceCollection) => {
    dispatch(globalLoadingOn());
    let resourceDictionary = JSON.parse(JSON.stringify(combinedDictionary));
    for (const entry of resourceCollection) {
        resourceDictionary[entry.id] = entry.revisedResourceJson;
    }
    let targetResources = [];
    let fliListToProcess = [];
    targetResources = netEffectContributions.map(section => {
        let targetfoi = section.focus?.reference?.replace("EvidenceVariable/", "");
        let targetfli;
        if (!targetfoi && section.focus?.identifier?.system === "https://fevir.net/FLI" && section.focus.identifier.value) {
            targetfli = section.focus.identifier.value;
        }
        let targettitle = section.focus?.display;
        if (!targetfoi && targetfli) {
            fliListToProcess.push(targetfli);
        }
        return {
            id: targetfoi,
            title: targettitle,
            fli: targetfli
        };
    });
    if (fliListToProcess.length > 0) {
        await addFoisFromFlisInTargetResources(targetResources, apiToken);
    }
    if (targetResources.length > 0) {
        let body = {
            findingsSection: netEffectContributions,
            combinedDictionary: resourceDictionary,
            resourceCollection: resourceCollection
        };
        dispatch(fetchOutcomeVariables(body));
    }
    dispatch(globalLoadingOff());
};

//mutates findingsSection and returns netEffectEstimateSection
const recalculateFindingsSectionFromReferences = async (findingsSection, ratingDictionary,
    combinedDictionary, dispatch, userName) => {

    let netEffectPointEstimate = 0;
    let netEffectStandardDeviationSquared = 0;
    for (let section of findingsSection) {
        if (section.section && section.focus?.reference?.replace("EvidenceVariable/", "")) {
            let targetFoi = section.focus.reference.replace("EvidenceVariable/", "");
            section.section[0].text = {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (section.focus?.display || section.title) + "</p></div>"
            };
            let relativeImportanceMultiplier = 0;
            let desirability = "undesirable";
            let desirabilityDisplay = "Presumed Undesirable";
            if (ratingDictionary[targetFoi]?.desirability === true) {
                desirabilityDisplay = "Desirable";
                desirability = 1;
            }
            if (ratingDictionary[targetFoi]?.desirability === false) {
                desirabilityDisplay = "Undesirable";
            }
            let importanceReference;
            if (ratingDictionary[targetFoi] &&
                (ratingDictionary[targetFoi].percentageValue || ratingDictionary[targetFoi].percentageValue === 0) &&
                !isNaN(ratingDictionary[targetFoi].percentageValue)) {
                section.section[1].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + desirabilityDisplay + "</p></div>"
                };
                if (section.section[1].entry) {
                    delete section.section[1].entry;
                }
                section.section[2].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + ratingDictionary[targetFoi].percentageValue + "%</p></div>"
                };
                if (section.section[2].entry) {
                    delete section.section[2].entry;
                }
                relativeImportanceMultiplier = ratingDictionary[targetFoi].percentageValue;
                importanceReference = { "display": "defined by " + userName }
            }
            if (!relativeImportanceMultiplier && section.section[2]?.entry?.[0]) {
                importanceReference = section.section[2].entry[0];
                let outcomeRatingFoi = getIdFromReference(section.section[2].entry[0]);
                let outcomeImportance = combinedDictionary[outcomeRatingFoi];
                if (outcomeImportance?.content?.length > 0) {
                    for (const content of outcomeImportance.content) {
                        if (content.type?.coding?.[0]?.code === "outcome-desirability") {
                            section.section[1].text = {
                                "status": "generated",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (content.classifier?.[0]?.coding?.[0]?.display || "Presumed Undesirable") + "</p></div>"
                            };
                            if (content.classifier?.[0]?.coding?.[0]?.display === "Desirable") {
                                desirability = 1;
                            }
                        } else if (content.type?.coding?.[0]?.code === "relative-importance") {

                            section.section[2].text = {
                                "status": "generated",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + content.quantity?.value + "%</p></div>"
                            };
                            if (content.quantity?.value) {
                                relativeImportanceMultiplier = content.quantity.value;
                            }
                        }
                    }
                }
            }
            let controlEventRate = NaN;
            let controlEventRateDisplay = "[Not a proportion.]";
            if (section.section[3]?.entry?.[0]) {
                let baselineRiskFoi = getIdFromReference(section.section[3].entry[0]);
                let baselineRiskEvidence = combinedDictionary[baselineRiskFoi];
                [controlEventRate, controlEventRateDisplay] = extractControlEventRate(baselineRiskEvidence, controlEventRate, controlEventRateDisplay);
                section.section[3].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
                };
            }
            let riskDifference = NaN;
            let riskDifferenceDisplay = '[Not a difference in proportions.]';
            let interventionEventRate = NaN;
            let interventionEventRateDisplay = '[Not a proportion.]';
            let riskDifferenceLowValue = NaN;
            let riskDifferenceLowValueDisplay = 'No lower bound';
            let riskDifferenceHighValue = NaN;
            let riskDifferenceHighValueDisplay = 'No upper bound';
            let comparativeEvidenceTitle;
            let comparativeEvidence;
            if (section.section[5]?.entry?.[0]) {
                let effectEstimateFoi = getIdFromReference(section.section[5].entry[0]);
                comparativeEvidence = combinedDictionary[effectEstimateFoi];
                comparativeEvidenceTitle = comparativeEvidence?.title;

                [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
                    riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
                    riskDifferenceHighValueDisplay] = extractRiskDifferenceAndInterventionEventRateForUser(controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
                        riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
                        riskDifferenceHighValueDisplay, comparativeEvidence, dispatch);

                section.section[4].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + interventionEventRateDisplay + "</p></div>"
                };
                section.section[5].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + riskDifferenceDisplay + "</p></div>"
                };
            }
            let [netEffectContribution, netEffectContributionLow,
                netEffectContributionHigh] = calculateNetEffectContributionWithConfidence(riskDifference,
                    relativeImportanceMultiplier, desirability, riskDifferenceLowValue, riskDifferenceHighValue);
            let netEffectContributionDisplay = createNetEffectContributionDisplay(netEffectContribution, netEffectContributionLow, netEffectContributionHigh);
            if (!isNaN(netEffectContribution)) {
                netEffectPointEstimate += netEffectContribution / 100;
            }
            if (!section.section[6].text ||
                section.section[6].text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
            ) {
                section.section[6].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
                };
                if (netEffectContributionDisplay !== "[Not calculable.]") {
                    //create or replace the NetEffectContribution Evidence Resource
                    let netEffectContributionEvidence = {
                        "resourceType": "Evidence",
                        "id": "",
                        "meta": {
                            "profile": [
                                "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contribution"
                            ]
                        },
                        "contained": [comparativeEvidence.contained],
                        "title": "NetEffectContribution for " + comparativeEvidenceTitle + " for " + userName,
                        "status": "active",
                        "author": [
                            {
                                "name": "Computable Publishing: Net Benefit Calculator"
                            },
                            {
                                "name": userName
                            }
                        ],
                        "publisher": "Computable Publishing LLC",
                        "contact": [
                            {
                                "telecom": [
                                    {
                                        "system": "email",
                                        "value": "support@computablepublishing.com"
                                    }
                                ]
                            }
                        ],
                        "copyright": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
                        "variableDefinition": [
                            comparativeEvidence.variableDefinition?.[0] ||
                            {
                                "description": "population definition missing in comparative evidence",
                                "variableRole": "population"
                            },
                            comparativeEvidence.variableDefinition?.[1] ||
                            {
                                "description": "exposure definition missing in comparative evidence",
                                "variableRole": "exposure"
                            },
                            comparativeEvidence.variableDefinition?.[2] ||
                            {
                                "description": "outcome definition missing in comparative evidence",
                                "variableRole": "outcome"
                            },
                            {
                                "extension": [
                                    {
                                        "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance",
                                        "valueReference": importanceReference
                                    }
                                ],
                                "variableRole": "covariate",
                                "roleSubtype": {
                                    "text": "Relative Value Multiplier"
                                },
                                "observed": {
                                    "display": "Relative Importance Multiplier"
                                }
                            }
                        ],
                        "statistic": [
                            {
                                "statisticType": {
                                    "coding": [
                                        {
                                            "system": "https://fevir.net/sevco",
                                            "code": "STATO:0000424",
                                            "display": "risk difference"
                                        }
                                    ]
                                },
                                "quantity": {
                                    "value": netEffectContribution
                                },
                                "attributeEstimate": [
                                    {
                                        "type": {
                                            "coding": [
                                                {
                                                    "system": "https://fevir.net/sevco",
                                                    "code": "STATO:0000196",
                                                    "display": "confidence interval"
                                                }
                                            ]
                                        },
                                        "level": 0.95,
                                        "range": {
                                            "low": {
                                                "value": netEffectContributionLow
                                            },
                                            "high": {
                                                "value": netEffectContributionHigh
                                            }
                                        }
                                    }
                                ],
                                "modelCharacteristic": [
                                    {
                                        "code": {
                                            "text": "Net effect contribution analysis"
                                        }
                                    }
                                ]
                            }
                        ]
                    };
                    if (!comparativeEvidence.contained) {
                        delete netEffectContributionEvidence.contained;
                    }
                }
            }
            if (!isNaN(netEffectContributionLow) && !isNaN(netEffectContributionHigh)) {
                let confidenceIntervalWidth = (netEffectContributionHigh - netEffectContributionLow) / 100;
                let standardDeviation = confidenceIntervalWidth / 3.92;
                let standardDeviationSquared = standardDeviation * standardDeviation;
                netEffectStandardDeviationSquared += standardDeviationSquared;
            }
        }
    }
    findingsSection.sort((a, b) => {
        let aValue = 999999999;
        let bValue = 999999999;
        if (a.section && a.section.length >= 7 && a.section[6].text && a.section[6].text.div) {
            let aStarterValue = a.section[6].text.div;
            aValue = parseFloat(aStarterValue.split(' (')[0]);
        }
        if (b.section && b.section.length >= 7 && b.section[6].text && b.section[6].text.div) {
            let bStarterValue = b.section[6].text.div;
            bValue = parseFloat(bStarterValue.split(' (')[0]);
        }
        if (aValue < bValue) {
            return 1;
        }
        if (aValue > bValue) {
            return -1;
        }
        return 0;
    })

    let netEffectPointEstimateSpan = valueSpanWithStyle(netEffectPointEstimate, 2, true);
    let netEffectDisplay = netEffectPointEstimateSpan;
    let netEffectLowerBound;
    let netEffectUpperBound;
    if (netEffectStandardDeviationSquared) {
        let netEffectStandardDeviation = Math.sqrt(netEffectStandardDeviationSquared);
        netEffectLowerBound = netEffectPointEstimate - (1.96 * netEffectStandardDeviation);
        let netEffectLowerBoundSpan = valueSpanWithStyle(netEffectLowerBound, 2, false);
        netEffectUpperBound = netEffectPointEstimate + (1.96 * netEffectStandardDeviation);
        let netEffectUpperBoundSpan = valueSpanWithStyle(netEffectUpperBound, 2, false);
        netEffectDisplay += " (" + netEffectLowerBoundSpan + " to " + netEffectUpperBoundSpan + ")";
    }
    let netEffectEstimateSection = {
        'title': 'Net Effect Estimate',
        'code': {
            "coding": [
                {
                    "system": "https://fevir.net/resources/CodeSystem/179423",
                    "code": "net-effect-estimate",
                    "display": "Net Effect Estimate"
                }
            ],
            'text': 'Net Effect Estimate'
        },
        'text': { 'status': 'generated', 'div': "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectDisplay + "</p></div>" }
    };

    if (netEffectDisplay === "<span><b>0</b></span>") {
        netEffectEstimateSection.text = {
            'status': 'empty',
            'div': "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not yet calculated.</p></div>"
        };
    }

    return netEffectEstimateSection;
}

const reloadFindingsSectionAndNetEffectEstimate = async (netEffectContributions,
    netEffectEstimateSectionState, ratingDictionary,
    combinedDictionary, dispatch, userName, setNetEffectEstimateSectionState) => {

    let findingsSection = JSON.parse(JSON.stringify(netEffectContributions));
    let startingNetEffectSection = JSON.parse(JSON.stringify(netEffectEstimateSectionState));
    let netEffectEstimateSection = await recalculateFindingsSectionFromReferences(findingsSection,
        ratingDictionary, combinedDictionary, dispatch, userName);
    if (JSON.stringify(startingNetEffectSection) !== JSON.stringify(netEffectEstimateSection)) {
        setNetEffectEstimateSectionState(netEffectEstimateSection);
    }
    let netEffectContributionsBody = { netEffectContributions: findingsSection };
    dispatch(loadNetEffectContributions(netEffectContributionsBody));
}

const colorSchemeSwitch = (div, colorScheme) => {
    if (colorScheme === "red-green") {
        return div;
    } else if (colorScheme === "orange-blue") {
        return div.replaceAll('style="color: #AA0000"', 'style="color: #AA6000FF"').replaceAll('style="color: #00AA00"', 'style="color: #0033AAFF"');
    } else if (colorScheme === "greyscale") {
        return div.replaceAll('style="color: #AA0000"', 'style="color: #999999"').replaceAll('style="color: #00AA00"', 'style="color: #555555"');
    }
    return
}

const SummaryOfNetEffectTableForUser = ({ setRemountTable, resourceJson,
    netEffectEstimateSectionState, setNetEffectEstimateSectionState }) => {
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const colorScheme = useSelector(selectColorScheme);
    const userName = useSelector(selectCurrentUserName);
    const resourceCollection = useSelector(state => selectAllResources(state));
    const ratingStatus = useSelector(selectUserImportanceRatingsStatus);
    const userRatings = useSelector(state => selectAllUserImportanceRatings(state));
    const ratingDictionary = {};
    for (let key in userRatings) {
        ratingDictionary[userRatings[key].id] = userRatings[key];
    }
    const outcomeVariablesStatus = useSelector(selectOutcomeVariablesStatus);
    const outcomeVariables = useSelector(state => selectAllOutcomeVariables(state));

    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const netEffectContributions = useSelector(state => selectAllNetEffectContributions(state));
    const netEffectContributionsStatus = useSelector(selectNetEffectContributionsStatus);

    const [triggerReloadState, setTriggerReloadState] = useState(false);


    useEffect(() => {
        if (resourceJson && netEffectContributionsStatus === "idle") {
            let findingsSection;
            let netEffectSection;
            if (Array.isArray(resourceJson.section)) {
                for (const section of resourceJson.section) {
                    if (section?.code?.coding?.[0]?.code === "summary-of-findings" &&
                        section.section.length > 0
                    ) {
                        findingsSection = JSON.parse(JSON.stringify(section.section));
                    }
                    if (section?.code?.coding?.[0]?.code === "net-effect-estimate") {
                        netEffectSection = JSON.parse(JSON.stringify(section));
                    }
                }
            }
            if (findingsSection) {
                let netEffectContributionsArray = findingsSection.map(section => {
                    let targetFoi = getIdFromReference(section.focus);
                    section.id = targetFoi;
                    section.itemChanged = false;
                    return section;
                });
                let netEffectContributionsBody = { netEffectContributions: netEffectContributionsArray };
                dispatch(loadNetEffectContributions(netEffectContributionsBody));
            }
            if (netEffectSection) {
                setNetEffectEstimateSectionState(netEffectSection);
            }
            if (findingsSection && !netEffectSection) {
                netEffectSection = {
                    "title": "Net Effect Estimate",
                    "code": {
                        "coding": [
                            {
                                "system": "https://fevir.net/resources/CodeSystem/179423",
                                "code": "net-effect-estimate",
                                "display": "Net Effect Estimate"
                            }
                        ],
                        "text": "Net Effect Estimate"
                    },
                    "text": {
                        "status": "generated",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not yet calculated.</p></div>"
                    }
                }
                setNetEffectEstimateSectionState(netEffectSection);
            }
        } else if (netEffectContributionsStatus === "succeeded" &&
            comparativeEvidenceMap?.combinedDictionary && outcomeVariablesStatus === "idle") {
            loadParameterResources(dispatch, netEffectContributions,
                apiToken, comparativeEvidenceMap.combinedDictionary, resourceCollection);
        } else if (outcomeVariablesStatus === "succeeded" && ratingStatus === "idle") {
            dispatch(fetchUserImportanceRatings());
        } else if (ratingStatus === "succeeded") {
            reloadFindingsSectionAndNetEffectEstimate(netEffectContributions,
                netEffectEstimateSectionState, ratingDictionary,
                comparativeEvidenceMap.combinedDictionary, dispatch, userName,
                setNetEffectEstimateSectionState);
        }
    }, [resourceJson, netEffectContributionsStatus, outcomeVariablesStatus, ratingStatus,
        comparativeEvidenceMap]);

    useEffect(() => {
        if (triggerReloadState === true && comparativeEvidenceMap?.combinedDictionary) {
            reloadFindingsSectionAndNetEffectEstimate(netEffectContributions,
                netEffectEstimateSectionState, ratingDictionary,
                comparativeEvidenceMap.combinedDictionary, dispatch, userName,
                setNetEffectEstimateSectionState);
            setTriggerReloadState(false);
            setRemountTable(prevState => prevState + 1);
        }
    }, [triggerReloadState]);

    let netEffectEstimate = netEffectEstimateSectionState?.text?.div || null;

    return <div>
        {ratingStatus === "succeeded" &&
            <div>
                {(outcomeVariables && Object.keys(outcomeVariables).length > 0) ?
                    <>
                        <RateRelativeImportanceForUser
                            setTriggerReloadState={setTriggerReloadState} />
                    </>
                    :
                    <>
                        <span onClick={() => {
                            loadParameterResources(dispatch, netEffectContributions,
                                apiToken, comparativeEvidenceMap.combinedDictionary, resourceCollection);
                        }}>Reload Source JSON</span>
                    </>
                }
                <br />
                <br />
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell key="outcome">
                                <p>Outcome</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="desirability">
                                <p>Desirability</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="importance">
                                <p>Relative Importance</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="control">
                                <p>Result Without Treatment</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="intervention">
                                <p>Result With Treatment (Calculated)</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="effect">
                                <p>Difference With Treatment (Confidence Interval)</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="impact">
                                <p>Health Impact (Confidence Interval)</p>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {netEffectContributions.map((row, rowIndex) => {
                            return <Table.Row key={rowIndex}>
                                {row.section.map((item, itemIndex) => {
                                    return <Table.Cell key={itemIndex}>
                                        <div>
                                            <span>
                                                <DisplayFromFHIRXhtml xhtml={colorSchemeSwitch(item.text.div, colorScheme)} />
                                            </span>
                                        </div>
                                    </Table.Cell>
                                })}
                            </Table.Row>
                        })}
                    </Table.Body>
                    <>{netEffectEstimate &&
                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell className="netEffectCell" textAlign='right'
                                    colSpan={7}>
                                    <b>Net Effect Estimate = <DisplayFromFHIRXhtml xhtml={colorSchemeSwitch(netEffectEstimate, colorScheme)} /></b>
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    }</>
                </Table>
            </div>}
    </div>
}

export default SummaryOfNetEffectTableForUser;
export { recalculateFindingsSectionFromReferences, loadParameterResources };